import {
  ASSESSMENT_CAREGIVER_READINESS_ITEMS,
  ASSESSMENT_CHILD_READINESS_ITEMS,
  ASSESSMENT_ENVIRONMENT_READINESS_ITEMS,
  ASSESSMENT_READINESS_VALUES,
} from '../../../../../store/treatmentPlanV3';

import type { AssessmentReadinessItemId } from '../../../../../store/treatmentPlanV3';

export const EMPTY_ASSESSMENT_BEHAVIOR_VALUES = {
  behavior: '',
  dsm5_symptom: '',
  parent_concerns: '',
} as const;

export const EMPTY_ASSESSMENT_METHOD_VALUES = {
  tool: '',
  assessment_date: null,
  rationale: '',
  selection_rationale: '',
  tool_description: '',
  client_score: '',
} as const;

export const EMPTY_ASSESSMENT_METHOD_VISUAL_VALUES = {
  title: '',
  analysis: '',
} as const;

export const ASSESSMENT_BEHAVIOR_TEXT_MIN_LENGTH = 16;

export const ASSESSMENT_METHOD_RICH_TEXT_MIN_LENGTH = 60;

export const ASSESSMENT_METHOD_VISUAL_TITLE_MIN_LENGTH = 8;

export const ASSESSMENT_READINESS_NOTES_MIN_LENGTH = 8;

export const ASSESSMENT_METHOD_VISUAL_MAX_SIZE_MB = 10;

export const ASSESSMENT_METHOD_VISUAL_FILE_NAME_PATTERN = '^[a-zA-Z0-9_., -]*$';

export const ASSESSMENT_METHOD_VISUAL_ACCEPT = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg', '.jpg'],
} as const;

export const MIN_CLIENT_AGE_FOR_AFLS = 6;

export const ASSESSMENT_TOOL_IDS = {
  VB_MAPP: 'VB-MAPP',
  ABLLS_R: 'ABLLS-R',
  AFLS: 'AFLS',
  FAST: 'FAST',
  PDDBI: 'PDDBI',
  PEAK: 'PEAK',
  QABF: 'QABF',
  SOCIALLY_SAVVY: 'Socially Savvy',
  VINELAND_3: 'Vineland-3',
  ASAP: 'ASAP',
  OMNIA: 'OMNIA',
  SSIS: 'SSIS',
  PSI: 'PSI',
} as const;

export const ASSESSMENT_TOOL_DESCRIPTIONS = {
  [ASSESSMENT_TOOL_IDS.VINELAND_3]:
    "The Vineland-3 is a standardized measure of adaptive behavior--the things that people do to function in their everyday lives. Whereas ability measures focus on what the examinee can do in a testing situation, the Vineland-3 focuses on what they actually do in daily life. Because it is a norm-based instrument, the examinee's adaptive functioning is compared to that of others their age.",
  [ASSESSMENT_TOOL_IDS.AFLS]:
    'The Assessment of Functional Living Skills (The AFLS) is an assessment, skill tracking system, and curriculum guide for skills that are essential for independence. The AFLS focuses on functional skills that are used in everyday for typical activities. The AFLS is composed of six unique assessment modules.',
  [ASSESSMENT_TOOL_IDS.VB_MAPP]:
    'VB-MAPP is an assessment and skills-tracking system to assess the language, learning and social skills of children with autism or other developmental disabilities. A strong focus of the VB-MAPP is language and social interaction, which are the predominant areas of weakness in children with autism.',
  [ASSESSMENT_TOOL_IDS.PDDBI]:
    'The PDDBI is an informant-based rating scale that is designed to assist in the assessment of children who have been diagnosed with a pervasive developmental disorder (PDD) as defined by the DSM-IV. PDD is characterised by severe and pervasive impairments in several areas of development (e.g. communication skills, reciprocal social interaction skills, presence of stereotypic behaviours/activities). Unlike existing assessments for autism/PDD, the PDDBI was developed to assess both problem behaviours as well as appropriate social, language, and learning/memory skills. It also was designed to provide age-standardised scores for both parent and teacher ratings.',
  [ASSESSMENT_TOOL_IDS.ABLLS_R]:
    'This Assessment of Basic Language and Learning Skills-Revised (ABLLS-R) assessment provides a comprehensive review of 544 skills from 25 skill areas, including language, social interaction, self-help, academic and motor skills that most typically-developing children acquire prior to entering kindergarten.  The assessment results allow parents and professionals to pinpoint obstacles that have been preventing a child from acquiring new skills and to develop a comprehensive, highly personalized, language-based curriculum. It guides parents and educators in the selection of developmentally- appropriate learning objectives and allows them to track the progress in acquiring critical learner skills on the Skills Tracking Grids.',
  [ASSESSMENT_TOOL_IDS.PEAK]:
    'The Promoting the Emergence of Advance Knowledge (PEAK) Comprehensive Assessment (PCA) is a standardized language and cognition assessment Developed by Dr. Mark Dixon.  The PCA consists of 344 test items across five subtests: Direct Training, Generalization, Equivalence, Transformation Expressive, and Transformation Receptive. Each section is delivered directly to the client by the administrator, and contains expressive, receptive, and motor performance items that are distributed equally across the various PCA subtests. There are four modules available in the PEAK relational training system, each one having its own specific targets.',
  [ASSESSMENT_TOOL_IDS.QABF]:
    'The Questions About Behavior Functions (QABF), developed by Matson and Vollmer, is an indirect measurement tool that consists of 25 interview questions focusing on five categories of behavioral functioning: Attention, Escape, Physical, Tangible, and Nonsocial.  Each item is scored based on a four-point likert-type rating scale ranging from Never, Rarely, Some, and Often in the relation to the presentation of maladaptive behaviors. The information gathered from this indirect assessment supports the identification of the hypothesized function.',
  [ASSESSMENT_TOOL_IDS.FAST]:
    'The Functional Analysis Screening Tool is an indirect functional behavior assessment tool consisting of 8 interview questions and  16 yes/no questions where information is obtained from a caregiver or an individual who knows the client well.  The information gathered from the assessment allows the behavior analyst to hypothesize the function of the client’s maladaptive behavior.',
  [ASSESSMENT_TOOL_IDS.SOCIALLY_SAVVY]:
    "The Socially Savvy Checklist serves multiple purposes. The checklist assists in identifying the learner's strengths and challenges across different social skill domains. The checklist also highlights areas for which intervention is appropriate to remedy social skills deficits, allowing practitioners to prioritize the skills to the specific need of each learner.  The socially savvy manual comprises specific play-based activities that help increase critical social skills in order to allow learners to enjoy their environment in a more meaningful way",
  [ASSESSMENT_TOOL_IDS.ASAP]:
    "The Empowering Autonomy: A Skills Assessment Protocol (ASAP) is an assessment protocol for skill development and tracking developed by neurodivergent (autistic/ADHD) behavior analysts. It features skills across several categories including verbal behavior, sensory, educational/vocational, interpersonal, time management, autonomy, and independent living. This assessment includes a barriers assessment to identify key areas to consider for support of skill acquisition for the learner. A social validity measure, which can be modified to the learner's language skills and preferences, is also included for the learner to self-report satisfaction with programming. In addition to tracking varied supports for the learner, there is a mediator skill grid that helps track and provide guidance for skills helpful for those serving learners who may be building targeted skills.",
  [ASSESSMENT_TOOL_IDS.OMNIA]:
    'The OMNIA Assessment is an indirect behavioral assessment completed through caregiver, teacher, or stakeholder report. The assessment evaluates a broad range of skill repertoires and behavioral domains that contribute to meaningful participation in daily life and overall adaptive functioning. Assessment results may be compared to age-based norms and are intended to supplement direct assessment and clinical observation. Domains assessed may include:\n• Verbal operants and communication skills\n• Generative Language and Relational Responding\n• Executive Functioning\n• Psychological Flexibility\n• Prosocial skills and social engagement',
} as const;

export const ASSESSMENT_TOOL_OPTIONS = [
  { value: ASSESSMENT_TOOL_IDS.ABLLS_R, label: ASSESSMENT_TOOL_IDS.ABLLS_R },
  { value: ASSESSMENT_TOOL_IDS.AFLS, label: ASSESSMENT_TOOL_IDS.AFLS },
  { value: ASSESSMENT_TOOL_IDS.FAST, label: ASSESSMENT_TOOL_IDS.FAST },
  { value: ASSESSMENT_TOOL_IDS.PDDBI, label: ASSESSMENT_TOOL_IDS.PDDBI },
  { value: ASSESSMENT_TOOL_IDS.PEAK, label: ASSESSMENT_TOOL_IDS.PEAK },
  { value: ASSESSMENT_TOOL_IDS.PSI, label: ASSESSMENT_TOOL_IDS.PSI },
  { value: ASSESSMENT_TOOL_IDS.QABF, label: ASSESSMENT_TOOL_IDS.QABF },
  { value: ASSESSMENT_TOOL_IDS.SOCIALLY_SAVVY, label: ASSESSMENT_TOOL_IDS.SOCIALLY_SAVVY },
  { value: ASSESSMENT_TOOL_IDS.SSIS, label: ASSESSMENT_TOOL_IDS.SSIS },
  { value: ASSESSMENT_TOOL_IDS.VB_MAPP, label: ASSESSMENT_TOOL_IDS.VB_MAPP },
  { value: ASSESSMENT_TOOL_IDS.VINELAND_3, label: ASSESSMENT_TOOL_IDS.VINELAND_3 },
  { value: ASSESSMENT_TOOL_IDS.ASAP, label: ASSESSMENT_TOOL_IDS.ASAP },
  { value: ASSESSMENT_TOOL_IDS.OMNIA, label: ASSESSMENT_TOOL_IDS.OMNIA },
] as const;

export const ALWAYS_MANDATORY_ASSESSMENT_TOOLS = [
  ASSESSMENT_TOOL_IDS.QABF,
  ASSESSMENT_TOOL_IDS.VINELAND_3,
] as const;

export const DIAGNOSTIC_REPORT_REVIEWED_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
] as const;

export const ASSESSMENT_DSM5_SYMPTOM_OPTIONS = [
  'Deficits in social-emotional reciprocity, ranging, for example, from abnormal social approach and failure of normal back-and-forth conversation; to reduced sharing of interests, emotions, or affect; to failure to initiate or respond to social interactions.',
  'Deficits in nonverbal communicative behaviors used for social interaction, ranging, for example, from poorly integrated verbal and nonverbal communication; to abnormalities in eye contact and body language or deficits in understanding and use of gestures; to a total lack of facial expressions and nonverbal communication.',
  'Deficits in developing, maintaining, and understanding relationships, ranging, for example, from difficulties adjusting behavior to suit various social contexts; to difficulties in sharing imaginative play or in making friends; to absences of interest in peers.',
  'Stereotyped or repetitive motor movements, use of objects, or speech (e.g., simple motor stereotypies, lining up toys or flipping objects, echolalia, idiosyncratic phrases).',
  'Insistence on sameness, inflexible adherence to routines, or ritualized patterns of verbal or nonverbal behavior (e.g., extreme distress at small changes, difficulties with transitions, rigid thinking patterns, greeting rituals, need to take same route or eat same food everyday).',
  'Highly restricted, fixated interests that are abnormal in intensity or focus (e.g., strong attachment to or preoccupation with unusual objects, excessively circumscribed or perseverative interest).',
  'Hyper- or hyporeactivity to sensory input or unusual interest in sensory aspects of the environment (e.g., apparent indifference to pain/temperature, adverse response to specific sounds or textures, excessive smelling or touching of objects, visual fascination with lights or movement).',
] as const;

export const ADDITIONAL_FILES_VIEWED_OPTIONS = [
  'Previous assessment reports',
  'Previous intervention plans and treatment plans',
  'Reports by other providers',
  'School reports',
  'Medical reports',
  'Other',
] as const;

export const ASSESSMENT_READINESS_ITEM_MAX_SCORE = 2;

export const ASSESSMENT_READINESS_OPTIONS = [
  ASSESSMENT_READINESS_VALUES.NOT_READY,
  ASSESSMENT_READINESS_VALUES.SOMEWHAT,
  ASSESSMENT_READINESS_VALUES.READY,
] as const;

export const ASSESSMENT_READINESS_ITEM_LABELS = {
  attends_to_screen: 'Attends to screen ≥ 30 seconds',
  follows_instructions: 'Follows telehealth instructions',
  caregiver_mediation: 'Tolerates caregiver mediation',
  no_severe_behavior: 'No severe SIB or aggression',
  communicates_needs: 'Communicates basic needs',
  reinforcers_available: 'Reinforcers available via caregiver/screen',
  available_during_sessions: 'Available during sessions',
  implements_directions: 'Implements BCBA directions',
  operates_tech: 'Operates tech independently',
  manages_safety: 'Manages safety appropriately',
  distraction_free_area: 'Prepares distraction-free area',
  reinforcers_ready: 'Has reinforcers ready',
  attends_reliably: 'Attends sessions reliably',
  stable_internet: 'Stable internet connection',
  camera_audio: 'Functional camera & audio',
  quiet_environment: 'Quiet environment',
  lighting: 'Good lighting / visibility',
  materials: 'Materials available',
  confidential_space: 'Confidential space',
} as const satisfies Record<AssessmentReadinessItemId, string>;

export const ASSESSMENT_READINESS_SECTIONS = [
  {
    id: 'caregiver',
    title: 'Caregiver readiness',
    items: ASSESSMENT_CAREGIVER_READINESS_ITEMS,
  },
  {
    id: 'child',
    title: 'Child readiness',
    items: ASSESSMENT_CHILD_READINESS_ITEMS,
  },
  {
    id: 'environment',
    title: 'Environmental readiness',
    items: ASSESSMENT_ENVIRONMENT_READINESS_ITEMS,
  },
] as const;

export const ASSESSMENT_READINESS_OVERALL_MAX_SCORE =
  ASSESSMENT_READINESS_SECTIONS.reduce((total, section) => total + section.items.length, 0) *
  ASSESSMENT_READINESS_ITEM_MAX_SCORE;

export const ASSESSMENT_BARRIER_OPTIONS = [
  { value: 'Family stress or caregiver burnout', label: 'Family stress or caregiver burnout' },
  { value: 'Transportation challenges', label: 'Transportation challenges' },
  { value: 'Financial hardship', label: 'Financial hardship' },
  { value: 'Insurance coverage limitations', label: 'Insurance coverage limitations' },
  {
    value: 'Scheduling conflicts with school, work, or activities',
    label: 'Scheduling conflicts with school, work, or activities',
  },
  { value: 'Limited understanding of ABA services', label: 'Limited understanding of ABA services' },
  {
    value: 'Language or communication barriers',
    label: 'Language or communication barriers',
  },
  {
    value: 'Cultural beliefs impacting treatment acceptance',
    label: 'Cultural beliefs impacting treatment acceptance',
  },
  {
    value: 'Difficulty maintaining consistent routines at home',
    label: 'Difficulty maintaining consistent routines at home',
  },
  {
    value: 'Frequent cancellations or missed appointments',
    label: 'Frequent cancellations or missed appointments',
  },
  {
    value: 'Limited support system within the family',
    label: 'Limited support system within the family',
  },
  {
    value: 'Technology or internet access issues for telehealth',
    label: 'Technology or internet access issues for telehealth',
  },
  {
    value: 'Client illness or medical complications',
    label: 'Client illness or medical complications',
  },
  {
    value: 'Medication changes affecting participation',
    label: 'Medication changes affecting participation',
  },
  { value: 'Sleep difficulties impacting engagement', label: 'Sleep difficulties impacting engagement' },
  {
    value: 'Sensory sensitivities interfering with sessions',
    label: 'Sensory sensitivities interfering with sessions',
  },
  {
    value: 'Challenging behaviors limiting participation in services',
    label: 'Challenging behaviors limiting participation in services',
  },
  {
    value: 'Difficulty transitioning between settings or providers',
    label: 'Difficulty transitioning between settings or providers',
  },
  {
    value: 'Caregiver mental health or personal stressors',
    label: 'Caregiver mental health or personal stressors',
  },
  {
    value: 'Lack of childcare for other children in the home',
    label: 'Lack of childcare for other children in the home',
  },
  {
    value: 'Concerns about privacy or services in the home',
    label: 'Concerns about privacy or services in the home',
  },
  { value: 'Limited motivation or readiness for change', label: 'Limited motivation or readiness for change' },
  {
    value: 'Communication deficits affecting expression of needs',
    label: 'Communication deficits affecting expression of needs',
  },
] as const;
